import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { InputEmail } from '../InputEmail';
import { InputPassword } from '../InputPassword';
import { ErrorNotice } from '../common/ErrorNotice';
import { INITIAL_LOGIN_PATTERN } from '@/constants/auth';
import { ROUTE_PATH } from '@/constants/routes';
import { MEMBERSHIP_TYPES } from '@/constants/users';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useAuth } from '@/hooks/useAuth';
import { useSuiteLoungeFlow } from '@/hooks/useSuiteLoungeFlow';
import { useSuiteLoungeSessionReset } from '@/hooks/useSuiteLoungeSessionReset';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { isSuiteLoungeFlow, withEntry } = useSuiteLoungeFlow();
  const { isMembershipUnknown, resetSession } = useSuiteLoungeSessionReset();
  const { translateApiError } = useApiErrorTranslation();
  const [loginError, setLoginError] = useState<{
    message: string | string[];
    detailMessages?: string[];
  } | null>(null);
  const { isRequesting, execute } = useApiRequest<'POST', 'UserLogin'>(
    'POST',
    'UserLogin',
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // 既にログイン済みの場合は予約画面にリダイレクト
  useEffect(() => {
    if (!isAuthenticated) return;

    // SL導線(?entry=sl)にAL資格のセッションで流入した場合はTOPへ通さずログアウトし、
    // SL会員としてログインし直してもらう(BE側で会員種別のアップグレードを走らせるため)
    if (isSuiteLoungeFlow) {
      // 会員種別が判明するまでは上限を設けずに待つ。
      // ここで打ち切ってTOPへ遷移するとこの画面がアンマウントされ、URLから ?entry=sl も
      // 失われるため、後から ana_lounge と判明しても再判定される機会が二度と来ない。
      // 待っている間もログインフォームは表示されており、判明しなければログインし直せば
      // 正しい導線に乗るので、待ち続けても手詰まりにはならない。
      if (isMembershipUnknown) return;
      if (resetSession()) return;
    }

    // この effect は会員種別の判明などで複数回走り得るため、push だと履歴に /top が重複して
    // ブラウザバックが効かなくなる。ログイン済みでログイン画面に戻る意味も無いので replace する。
    navigate(ROUTE_PATH.TOP, { replace: true });
  }, [
    isAuthenticated,
    isSuiteLoungeFlow,
    isMembershipUnknown,
    resetSession,
    navigate,
  ]);

  const onFormSubmit = async (data: LoginFormData) => {
    setLoginError(null);

    const requestData = {
      ...data,
      membership_type: isSuiteLoungeFlow
        ? MEMBERSHIP_TYPES.SUITE_LOUNGE
        : MEMBERSHIP_TYPES.ANA_LOUNGE,
    };

    const result = await execute(requestData);

    if (result.ok) {
      const responseData = result.data.data;

      // 初回ログイン時の分岐処理
      if ('initial_login_pattern' in responseData) {
        const { initial_login_pattern } = responseData;

        // initial_login_pattern に応じて遷移先を変更
        if (
          initial_login_pattern === INITIAL_LOGIN_PATTERN.WORKHUB_NO_CUSTOMER
        ) {
          // WorkhubのANA組織内に会員情報がない場合は氏名が必要なため、氏名入力画面へ
          navigate(withEntry(ROUTE_PATH.HAS_BITKEY_ACCOUNT), {
            state: { email: data.email, initial_login_pattern },
          });
        } else if (
          initial_login_pattern ===
            INITIAL_LOGIN_PATTERN.WORKHUB_EXISTING_CUSTOMER ||
          initial_login_pattern === INITIAL_LOGIN_PATTERN.TERMS_NOT_ACCEPTED
        ) {
          // WorkhubのANA組織内に会員情報がある場合、または利用規約未同意の場合はリニューアル告知画面へ
          navigate(ROUTE_PATH.RENEWAL_NOTICE, {
            state: { email: data.email, initial_login_pattern },
          });
        }
        return;
      }

      // 通常のログイン処理
      const { jwt, expires_at, role } = responseData;

      // AuthContextにログイン情報を保存（user情報は後で取得）
      await login(jwt, expires_at, role);

      // TOP画面にリダイレクト
      navigate(ROUTE_PATH.TOP);
    } else {
      const { message, errorKey, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      // 認証失敗（401）は汎用タイトル「認証が必要です」を出さず、詳細メッセージのみ表示する
      const showsDetailOnly =
        errorKey === 'http.unauthorized' && detailMessages.length > 0;
      setLoginError({
        message: showsDetailOnly ? detailMessages : message,
        detailMessages: showsDetailOnly ? undefined : detailMessages,
      });
    }
  };

  return (
    <section className="l-card">
      <h2 className="l-card__title">{t('login.title')}</h2>
      <div className="c-auth c-auth--login">
        <form className="c-auth__form" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="c-auth__fields">
            <div className="c-auth__field">
              <label className="c-auth__label">
                <span className="c-auth__label-text">{t('login.email')}</span>
                <InputEmail
                  className="c-auth__input"
                  placeholder={t('login.emailPlaceholder')}
                  {...register('email', {
                    required: t('login.validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('login.validation.emailInvalid'),
                    },
                  })}
                />
              </label>
              {errors.email && (
                <p className="c-auth__alert c-auth__alert--error">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="c-auth__field">
              <label className="c-auth__label">
                <span className="c-auth__label-text">
                  {t('login.password')}
                </span>
                <InputPassword
                  className="c-auth__input"
                  placeholder={t('login.passwordPlaceholder')}
                  {...register('password', {
                    // ログイン画面では長さ・文字種を検証しない。
                    // 既に存在するパスワードを入力する場所であり、Workhub側のポリシーが
                    // 変わると正しいパスワードを持つ利用者がログイン不能になるため。
                    // 検証はWorkhubの責務とし、ここでは未入力のみを弾く。
                    required: t('login.validation.passwordRequired'),
                  })}
                />
              </label>
              {errors.password && (
                <p className="c-auth__alert c-auth__alert--error">
                  {errors.password.message}
                </p>
              )}
              <Link
                className="c-auth__link c-auth__link--forgot"
                to={withEntry(ROUTE_PATH.PASSWORD_RESET)}
              >
                {t('login.forgotPassword')}
              </Link>
            </div>
          </div>
          <div className="c-auth__actions">
            <button
              className="c-auth__btn c-auth__btn--login"
              type="submit"
              disabled={isRequesting}
            >
              {isRequesting
                ? t('login.loginButtonLoading')
                : t('login.loginButton')}
            </button>
          </div>
          {loginError && (
            <div className="c-auth__alerts">
              <ErrorNotice
                message={loginError.message}
                details={loginError.detailMessages}
              />
            </div>
          )}
        </form>
      </div>
    </section>
  );
};
